import {
  SEARCH_PLAYLIST_TAGS_FILTER_FIELD,
  SEARCH_TRACK_TAGS_FILTER_FIELD,
} from '@disco/constants/search'
import { SORT_DIRECTION_DESC } from '@disco/constants/SortTypes'
import { endOfDay, isSameDay, startOfDay } from 'date-fns'

import { getFiltersWithValidValues } from './search'
import {
  extractGroupIdsFromFilters,
  hasAllGroupsSelection,
  transformFiltersToV2,
} from './search-page'

import type { SearchFilter, SearchFilterValue } from './search'
import type { FilterState } from 'types/filter-page'
import type {
  SourceScope,
  V2Filter,
  V2FilterGroup,
  V2SearchQuery,
} from 'types/search-page'

export type GlobalSearchType = '' | 'track' | 'playlist' | 'album' | 'artist'

// Legacy metadata filter rows from the global-search reducer
// (`state.search.filters`).
export type GlobalSearchFilter = SearchFilter

export type GlobalSearchV2Input = {
  searchTerm: string
  type: GlobalSearchType | null
  sort: string
  sortDirection: string | null
  groups: number[]
  tags: string[]
  nativeTags: number[]
  filters: GlobalSearchFilter[]
  startDate: Date | null
  endDate: Date | null
  duration: string | null
  simSearchUrl?: string | null
  showcasesEnabled?: boolean
  size: number
  scope: SourceScope[]
  cursor?: string
  /** Correlates the parallel business and mds requests of one search. */
  searchId: string
  /** STL-1250: shared search page filters; when set they supersede the
   * legacy filters, tags, nativeTags, groups, dates and duration inputs. */
  filterState?: FilterState[] | null
}

// Mirrors the legacy `disco_*` query params the backend exposed as dedicated
// filter fields (see l2clientfacing/search/filters.py `from_legacy_args`).
const DISCO_FILTER_FIELDS: Record<string, string> = {
  comment: 'notes',
  client_comment: 'client_notes',
  internal_comment: 'internal_notes',
}

// "more than 2000" means >= 2001, matching the legacy `adjustFilterValue`
// behaviour; non-numeric values (including arrays) pass through unadjusted.
const adjustFilterValue = (
  value: SearchFilterValue | undefined,
  delta: -1 | 1,
): SearchFilterValue | undefined => {
  if (
    typeof value === 'number' ||
    (typeof value === 'string' && /^[0-9]+$/.test(value))
  ) {
    const numeric = typeof value === 'number' ? value : parseInt(value)
    return (numeric + delta).toString()
  }
  return value
}

const metadataFilterToV2 = (filter: GlobalSearchFilter): V2Filter => {
  const { key, value, type, start, end } = filter

  if (type === 'range') {
    return { field: key, operator: 'between', value: [start, end] }
  }
  if (type === 'less than' || type === 'before') {
    return { field: key, operator: 'lte', value: adjustFilterValue(value, -1) }
  }
  if (type === 'more than' || type === 'after') {
    return { field: key, operator: 'gte', value: adjustFilterValue(value, 1) }
  }
  return {
    field: key,
    operator: Array.isArray(value) ? 'equals' : 'contains',
    value,
  }
}

const durationToV2 = (duration: string): V2Filter | null => {
  const [rawMin, rawMax] = duration.split('-')
  const min = rawMin ? parseInt(rawMin) : null
  const max = rawMax ? parseInt(rawMax) : null

  if (min && max) {
    return { field: 'duration', operator: 'between', value: [min, max] }
  }
  if (min) return { field: 'duration', operator: 'gte', value: min }
  if (max) return { field: 'duration', operator: 'lte', value: max }
  return null
}

const dateRangeToV2 = (
  startDate: Date | null,
  endDate: Date | null,
): V2Filter | null => {
  if (!startDate || !endDate) return null

  const allTime =
    (startDate.getTime() === new Date(0).getTime() ||
      startDate.getTime() < 0) &&
    isSameDay(endDate, new Date())
  if (allTime) return null

  return {
    field: 'date',
    operator: 'between',
    value: [
      startOfDay(startDate).toISOString(),
      endOfDay(endDate).toISOString(),
    ],
  }
}

type BuiltV2Filters = {
  v2Filters: V2Filter[]
  /** Drives the `is_playlist`-first sort. */
  metadataFiltersSet: boolean
  /** Track tags only exist on tracks, so they force `result_types`. */
  trackTagsSet: boolean
}

// Filters that are not metadata, so they must not drive the metadata sort.
const NON_METADATA_FILTER_FIELDS = new Set<string>([
  SEARCH_TRACK_TAGS_FILTER_FIELD,
  SEARCH_PLAYLIST_TAGS_FILTER_FIELD,
  'duration',
  'date',
  'playlist_types',
])

const buildLegacyV2Filters = ({
  type,
  filters,
  nativeTags,
  tags,
  duration,
  startDate,
  endDate,
}: Pick<
  GlobalSearchV2Input,
  | 'type'
  | 'filters'
  | 'nativeTags'
  | 'tags'
  | 'duration'
  | 'startDate'
  | 'endDate'
>): BuiltV2Filters => {
  const v2Filters: V2Filter[] = []

  if (type === 'album') {
    v2Filters.push({
      field: 'playlist_types',
      operator: 'equals',
      value: 'useralbum',
    })
  }

  const metaFilters = getFiltersWithValidValues(
    filters.filter(filter => !filter.key.startsWith('disco_')),
  )
  metaFilters.forEach(filter => v2Filters.push(metadataFilterToV2(filter)))

  filters
    .filter(filter => filter.key.startsWith('disco_') && filter.value)
    .forEach(({ key, value }) => {
      const legacyParam = key.replace(/^disco_/, '')
      v2Filters.push({
        field: DISCO_FILTER_FIELDS[legacyParam] ?? legacyParam,
        operator: 'contains',
        value,
      })
    })
  const metadataFiltersSet = v2Filters.length > (type === 'album' ? 1 : 0)

  if (nativeTags.length) {
    v2Filters.push({
      field: SEARCH_TRACK_TAGS_FILTER_FIELD,
      operator: 'equals',
      value: nativeTags,
    })
  }
  if (tags.length) {
    v2Filters.push({
      field: SEARCH_PLAYLIST_TAGS_FILTER_FIELD,
      operator: 'equals',
      value: tags,
    })
  }

  const durationFilter = duration ? durationToV2(duration) : null
  if (durationFilter) v2Filters.push(durationFilter)

  const dateFilter = dateRangeToV2(startDate, endDate)
  if (dateFilter) v2Filters.push(dateFilter)

  return { v2Filters, metadataFiltersSet, trackTagsSet: nativeTags.length > 0 }
}

// STL-1250: the search page's own FilterState[] and converter, with only the
// Browse-specific album/sort/result-type semantics applied on top.
const buildFilterStateV2Filters = (
  filterState: FilterState[],
  type: GlobalSearchType | null,
): BuiltV2Filters => {
  const v2Filters: V2Filter[] = []

  if (type === 'album') {
    v2Filters.push({
      field: 'playlist_types',
      operator: 'equals',
      value: 'useralbum',
    })
  }

  const transformed = transformFiltersToV2(filterState)
  const entries = (transformed?.filters ?? []) as V2Filter[]
  v2Filters.push(...entries)

  return {
    v2Filters,
    metadataFiltersSet: entries.some(
      entry => !NON_METADATA_FILTER_FIELDS.has(entry.field),
    ),
    trackTagsSet: entries.some(
      entry => entry.field === SEARCH_TRACK_TAGS_FILTER_FIELD,
    ),
  }
}

/**
 * Builds the `POST /api/v2/search/` request body for the global search bar
 * from the legacy global-search redux state, preserving the semantics of the
 * legacy `GET /api/search/` query construction in `SearchActions`.
 */
export const buildGlobalV2SearchQuery = (
  input: GlobalSearchV2Input,
): V2SearchQuery => {
  const {
    searchTerm,
    type,
    sort,
    sortDirection,
    groups,
    tags,
    nativeTags,
    filters,
    startDate,
    endDate,
    duration,
    simSearchUrl,
    showcasesEnabled,
    size,
    scope,
    cursor,
    searchId,
    filterState,
  } = input

  const { v2Filters, metadataFiltersSet, trackTagsSet } = filterState
    ? buildFilterStateV2Filters(filterState, type)
    : buildLegacyV2Filters({
        type,
        filters,
        nativeTags,
        tags,
        duration,
        startDate,
        endDate,
      })

  const searchInGroups = filterState
    ? extractGroupIdsFromFilters(filterState) ?? []
    : groups
  const allChannels = filterState
    ? hasAllGroupsSelection(filterState, 'look_in_channels')
    : false
  const allInboxes = filterState
    ? hasAllGroupsSelection(filterState, 'look_in_inboxes')
    : false

  const isSimSearch = Boolean(simSearchUrl)
  // Legacy forced type=track when native tags were set; the backend also
  // forces tracks for simsearch.
  let resultTypes: string[]
  if (isSimSearch || trackTagsSet) {
    resultTypes = ['track']
  } else if (type === 'album' || type === 'playlist') {
    resultTypes = ['playlist']
  } else if (type) {
    resultTypes = [type]
  } else {
    resultTypes = showcasesEnabled
      ? ['artist', 'playlist', 'track']
      : ['playlist', 'track']
  }

  const sortParam =
    !sortDirection || sortDirection === SORT_DIRECTION_DESC ? `-${sort}` : sort
  let sortOrder: string[]
  if (isSimSearch) {
    sortOrder = ['-score']
  } else if (type || trackTagsSet) {
    sortOrder = [sortParam]
  } else if (metadataFiltersSet) {
    // Track metadata filters without a type: ask for tracks first.
    sortOrder = ['is_playlist', sortParam]
  } else {
    sortOrder = showcasesEnabled
      ? ['-is_playlist', '-is_track', sortParam]
      : ['-is_playlist', sortParam]
  }

  return {
    query: isSimSearch ? (simSearchUrl as string) : searchTerm,
    size,
    type: isSimSearch ? 'simsearch' : 'regular',
    scope,
    sort: sortOrder,
    result_types: resultTypes,
    event_data: {
      context: 'Browse',
      albums_only: type === 'album',
      search_id: searchId,
    },
    ...(cursor && { cursor }),
    ...((searchInGroups.length > 0 || allChannels || allInboxes) && {
      search_in: {
        ...(searchInGroups.length > 0 && { groups: searchInGroups }),
        groups_descendants: true,
        include_playlist_tracks: true,
        ...(allChannels && { all_channels: true }),
        ...(allInboxes && { all_inboxes: true }),
      },
    }),
    ...(v2Filters.length > 0 && {
      filters: { operator: 'and', filters: v2Filters } as V2FilterGroup,
    }),
  }
}
